import {
    AnalyticsCustomEvent,
    AnalyticsEcommerceEvent,
    BaseCustomEvent,
    CustomEvent,
    Dimension,
    Ecommerce,
    Metric,
    PageViewEvent,
    TicketItem,
    WindowInterface
} from "./entity";
import { EcommerceData } from "./entity/base/BaseEcommerceData.interface";
import { GAEcommerceEvent, GAEvent } from "./enum";
import { DateTimeZone, SearchResultEvents } from "webshop-api";
import { ItemType } from "./item-type.enum";
import { DateTimeUtil } from '../date-time';
import { AnalyticsStorage } from "./storage";
import { AnalyticsSessionStore } from "../storage";

export class GTMUtils {

    public static pushEcommerceToDataLayer(ecommerceObj: Ecommerce, eventName?: GAEcommerceEvent | GAEvent) {
        this.pushToDataLayer({ecommerce: ecommerceObj, event: eventName});
    }

    public static pushGA4EcommerceToDataLayer<T extends EcommerceData>(ecommerceObj: AnalyticsEcommerceEvent<T>) {
        this.pushToDataLayer(ecommerceObj);
    }

    public static pushPageViewToDataLayer(pageView: PageViewEvent) {
        this.pushToDataLayer({
            event: GAEvent.PageView,
            ...pageView,
        });
    }

    //TODO: remove this old UA custom event implementation
    public static pushCustomEventToDataLayer(customEvent: CustomEvent) {
        this.pushToDataLayer({
            event: GAEvent.Custom,
            ...customEvent,
        });
    }

    public static pushAnalyticsEventToDataLayer(event: AnalyticsCustomEvent) {
        this.pushToDataLayer(event);
    }

    public static pushPageTrackingDataToDataLayer(data: BaseCustomEvent) {
        this.pushToDataLayer(data);
    }

    public static setDimension(dimension: Dimension) {
        this.pushToDataLayer(
            Object.defineProperty({}, dimension.dimensionName, {value: dimension.value, enumerable: true})
        );
    }

    public static setMetric(metric: Metric) {
        this.pushToDataLayer(
            Object.defineProperty({}, metric.metricName, {value: metric.value, enumerable: true})
        );
    }

    public static formatToAnalyticsDateFrom(data: string | string[]) {
        const dates = Array.isArray(data) ? data : data.split(',');

        const result = dates.map(date => {
            const jsDate = new Date(date);
            const day = jsDate.getDate().toString();
            const month = (jsDate.getMonth() + 1).toString();
            const year = jsDate.getFullYear();

            return `${day}.${month}.${year}`;
        });

        return result.length === 1 ? result[0] : result;
    }

    public static calculateDaysUntilEventFrom(data: string | string[]) {
        const dates = Array.isArray(data) ? data : data.split(',');

        const result = dates.map(date => DateTimeUtil.getDaysDifference(date));

        return result.length === 1 ? result[0] : result;
    }

    public static parseItemIdFromCurrentUrl() {
        const url = window.location.href;

        return this.parseItemIdFrom(url);
    }

    public static isStoredItemListNameValidFor(itemId: string) {
        const analyticsStorage = AnalyticsStorage.getInstance();
        const storedData = analyticsStorage!.getSessionStore()

        if (!storedData) {
            return false;
        }

        const list = storedData.itemLists && storedData.itemLists!.find(list => list.name === storedData.activeList);

        return !!list && !!list.items.find(item => item.item_id === itemId);
    }

    public static leadsToPerformancePage(item: HTMLElement): boolean {
        const hrefValue = item.getAttribute('href') ?? null;

        return !!(hrefValue && hrefValue.includes('performance.html'));
    }

    public static storeActiveItemListName(listName: string) {
        const analyticsStorage = AnalyticsStorage.getInstance();

        if (analyticsStorage && analyticsStorage.getSessionStore()) {
            analyticsStorage.pushDataToSessionStorage({activeList: listName});
        }
    }

    public static parseItemIdFrom(url: string | null): string | null {
        if (!url) {
            return null;
        }

        const segments = url.split('/');
        const idSegment = segments[segments.length - 2];

        if (!idSegment) {
            return null;
        }

        const lastDashIndex = idSegment.lastIndexOf('-');

        if (lastDashIndex === -1) {
            return null;
        }

        const id = idSegment.substring(lastDashIndex + 1);

        return id;
    }

    public static getItemTypeFrom(url: string) {
        if (url.includes('performance.html')) {
            return ItemType.Event;
        }

        if (url.includes('event.html')) {
            return ItemType.EventSeries;
        }

        if (url.includes('profile.html')) {
            return ItemType.Artist;
        }

        if (url.includes('promo.html')) {
            return ItemType.Campaign;
        }

        if (url.includes('city.html')) {
            return ItemType.City;
        }

        if (url.includes('venue.html')) {
            return ItemType.Venue;
        }

        return ItemType.Miscellaneous;
    }

    public static hasPerformanceInEventList(elements: HTMLElement[]): boolean {
        return elements.some(item => {
            const url = this.getEventListItemUrl(item);

            return url && url.includes('performance.html');
        })
    }

    public static isOnArtistEventseriesOrCityPage(): boolean {
        const cityPage = document.querySelector('[data-js=city-page]');
        const eventSeriesSection = document.querySelector('[data-js=event-series-section]');
        const artistPage = document.querySelector('[data-js=artist-page]');

        return !!(cityPage || eventSeriesSection || artistPage);
    }

    public static getEventListItemUrl(element: HTMLElement) {
        let url = element.getAttribute('href');

        if (!url) {
            const childElement = element.querySelector('a');
            childElement ? url = childElement.getAttribute('href') : null;
        }

        return url;
    }

    public static attachEventSpecificDataTo(item: TicketItem, events: SearchResultEvents[]): void {
        const cities = events.map(event => event.city);
        const venues = events.map(event => event.venue);

        item.item_event_date = (events[0].dateStart as unknown as DateTimeZone).dateTime.toString();
        item.item_event_city = [...new Set(cities)].join(',');
        item.item_event_venue = [...new Set(venues)].join(',');
    }

    public static storeItemList(items: TicketItem[]) {
        const analyticsStorage = AnalyticsStorage.getInstance();

        if (analyticsStorage && analyticsStorage.getSessionStore()) {
            const listName = items[0]!.item_list_name;
            const itemLists = (<AnalyticsSessionStore>analyticsStorage.getSessionStore()).itemLists;
            const isListStored = itemLists ?
                !!itemLists.find(list => list.name === listName)
                : false;

            if (!isListStored) {
                analyticsStorage.pushItemListToSessionStorage({
                    name: listName,
                    items: <TicketItem[]>items,
                });
            }
        }
    }

    private static get assertDataLayer(): any[] {
        const windowObject = <WindowInterface> window;
        windowObject.dataLayer = windowObject.dataLayer || [];

        return windowObject.dataLayer;
    }

    private static pushToDataLayer(data: object) {
        const dataLayer = this.assertDataLayer;

        dataLayer.push(this.toLowerCaseValues(data));
    }

    private static toLowerCaseValues<T>(obj: T): T {
        if (Array.isArray(obj)) {
            return obj.map(item => this.toLowerCaseValues(item)) as unknown as T;
        } else if (obj !== null && typeof obj === 'object') {
            const result: any = {};
            Object.keys(obj).forEach(key => {
                result[key] = this.toLowerCaseValues((obj as any)[key]);
            });
            return result;
        } else if (typeof obj === 'string') {
            return obj.toLowerCase() as unknown as T;
        } else {
            return obj;
        }
    }
}
